import Breadcrumb from '@/components/base/Breadcrumb';
import { Link } from 'react-router-dom';

interface ContentBlock {
  image: string;
  imageAlt: string;
  imagePosition?: 'left' | 'right';
  children: React.ReactNode;
}

interface CardGrid {
  title: string;
  subtitle: string;
  cards: {
    icon: string;
    title: string;
    description: string;
    link: string;
  }[];
}

interface ServicePageLayoutProps {
  title: string;
  subtitle: string;
  breadcrumb: { label: string; to?: string }[];
  jsonLdService: Record<string, unknown>;
  jsonLdFAQ: Record<string, unknown>;
  children?: React.ReactNode;
  contentBlocks?: ContentBlock[];
  cardGrids?: CardGrid[];
  fullWidthSections?: React.ReactNode[];
  heroImage?: string;
  heroImageAlt?: string;
  ctaTitle?: string;
  ctaText?: string;
  ctaServiceLabel?: string;
}

export default function ServicePageLayout({
  title,
  subtitle,
  breadcrumb,
  jsonLdService,
  jsonLdFAQ,
  children,
  contentBlocks,
  cardGrids,
  fullWidthSections,
  heroImage,
  heroImageAlt,
  ctaTitle,
  ctaText,
  ctaServiceLabel,
}: ServicePageLayoutProps) {
  const finalCtaTitle = ctaTitle || 'Une baie coulissante à réparer dans l\'Hérault ?';
  const finalCtaText = ctaText || 'Devis gratuit, intervention sous 24 à 48h, garantie 10 ans. Nos techniciens se déplacent dans tout l\'Hérault, 7j/7.';
  const finalServiceLabel = ctaServiceLabel || 'Voir tous nos services';

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLdService)}</script>
      <script type="application/ld+json">{JSON.stringify(jsonLdFAQ)}</script>

      {/* Hero section */}
      <section className="relative pt-20 pb-10 md:pt-28 md:pb-16 overflow-hidden">
        {heroImage ? (
          <>
            <img
              src={heroImage}
              alt={heroImageAlt || title}
              className="absolute inset-0 w-full h-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-primary-500/70"></div>
          </>
        ) : (
          <div className="absolute inset-0 bg-primary-500"></div>
        )}
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumb} light />
          <h1 className="text-xl md:text-3xl lg:text-4xl font-heading font-bold text-white mt-3 mb-3 leading-tight">
            {title}
          </h1>
          <p className="text-[#DCE8F2] text-sm md:text-lg font-body max-w-2xl leading-relaxed">
            {subtitle}
          </p>
        </div>
      </section>

      {/* Content blocks */}
      {contentBlocks && contentBlocks.length > 0 ? (
        <>
          {contentBlocks.map((block, index) => {
            const isLeft = block.imagePosition !== 'right';
            return (
              <section key={index} className={`py-10 md:py-16 ${index % 2 === 0 ? 'bg-background-50' : 'bg-background-100'}`}>
                <div className="max-w-7xl mx-auto px-4 md:px-6">
                  <div className={`flex flex-col ${isLeft ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-8 md:gap-12 items-start`}>
                    <div className="w-full lg:w-1/2 lg:self-stretch">
                      <img
                        src={block.image}
                        alt={block.imageAlt}
                        className="w-full h-56 md:h-72 lg:h-full object-cover rounded-lg"
                        loading="lazy"
                      />
                    </div>
                    <div className="w-full lg:w-1/2">
                      <div className="prose prose-lg max-w-none font-body text-foreground-500">
                        {block.children}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            );
          })}
        </>
      ) : (
        <section className="py-10 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <article className="prose prose-lg max-w-none font-body text-foreground-500">
              {children}
            </article>
          </div>
        </section>
      )}

      {/* Card grids - grilles de services */}
      {cardGrids && cardGrids.length > 0 && cardGrids.map((grid, gridIdx) => (
        <section key={`grid-${gridIdx}`} className={`py-10 md:py-16 ${contentBlocks && contentBlocks.length > 0 && gridIdx % 2 === 0 ? 'bg-background-100' : 'bg-background-50'}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-primary-500 mb-3">
              {grid.title}
            </h2>
            <p className="text-foreground-500 font-body text-base mb-8 max-w-3xl">
              {grid.subtitle}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {grid.cards.map((card, cardIdx) => (
                <Link
                  key={cardIdx}
                  to={card.link}
                  className="group bg-background-50 border border-background-200/70 rounded-lg p-5 hover:border-accent-500/30 transition-colors duration-200 flex flex-col"
                >
                  <span className="w-9 h-9 flex items-center justify-center text-accent-500 bg-accent-100 rounded-md mb-3 group-hover:bg-accent-500 group-hover:text-background-50 transition-colors duration-200">
                    <i className={`${card.icon} text-base`}></i>
                  </span>
                  <h3 className="text-base font-heading font-bold text-primary-500 mb-2 group-hover:text-accent-500 transition-colors duration-200">
                    {card.title}
                  </h3>
                  <p className="text-sm font-body text-foreground-500 leading-relaxed flex-grow">
                    {card.description}
                  </p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-accent-500 mt-3 whitespace-nowrap group-hover:gap-2 transition-all duration-200">
                    En savoir plus <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-line text-xs"></i></span>
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* Full-width sections */}
      {fullWidthSections && fullWidthSections.length > 0 && fullWidthSections.map((section, secIdx) => (
        <section key={`fw-${secIdx}`} className={`py-10 md:py-16 ${secIdx % 2 === 0 ? 'bg-background-100' : 'bg-background-50'}`}>
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="prose prose-lg max-w-[900px] mx-auto font-body text-foreground-500">
              {section}
            </div>
          </div>
        </section>
      ))}

      {/* Réassurance */}
      <section className="bg-background-50 py-6 md:py-10 border-y border-background-200/60">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-wrap justify-center gap-4 md:gap-12 text-xs md:text-sm font-body text-secondary-500 font-semibold">
            <span className="flex items-center gap-2 whitespace-nowrap"><span className="w-5 h-5 flex items-center justify-center text-accent-500"><i className="ri-checkbox-circle-fill"></i></span> Devis gratuit</span>
            <span className="flex items-center gap-2 whitespace-nowrap"><span className="w-5 h-5 flex items-center justify-center text-accent-500"><i className="ri-shield-check-fill"></i></span> Garantie 10 ans</span>
            <span className="flex items-center gap-2 whitespace-nowrap"><span className="w-5 h-5 flex items-center justify-center text-accent-500"><i className="ri-timer-flash-fill"></i></span> 24-48h</span>
            <span className="flex items-center gap-2 whitespace-nowrap"><span className="w-5 h-5 flex items-center justify-center text-accent-500"><i className="ri-calendar-check-fill"></i></span> 7j/7</span>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-background-100 py-10 md:py-16 pb-24 md:pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-lg md:text-2xl font-heading font-bold text-primary-500 mb-3">
            {finalCtaTitle}
          </h2>
          <p className="text-secondary-500 font-body text-sm md:text-base mb-6 max-w-lg mx-auto">
            {finalCtaText}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:+33980802242" className="btn-call text-sm px-6 py-3">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line text-sm"></i>
              </span>
              Appeler le 09 80 80 22 42
            </a>
            <Link to="/" className="btn-devis text-sm px-6 py-3">
              {finalServiceLabel}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}